import { useState } from 'react'
import { initials } from '../app/initials'
import { newUuid } from '../offline/uuid'
import { CallIconLink } from './CallLink'
import { CustomerDetailPane } from './CustomerDetailPane'
import { CustomerForm, type CustomerFields } from './CustomerForm'
import { useCreateCustomer, useCustomersList } from './useCustomers'

const EMPTY_CUSTOMER: CustomerFields = { name: '', phone: '', email: '', address: '' }

/**
 * Kunden: Master-Detail (.wf-split) wie der Alt-Monolith. Links die durchsuchbare Kundenliste, rechts der
 * gewählte Kunde (Stammdaten + Fahrzeuge + Auftragshistorie) oder das Anlegen-Formular.
 *
 * Die Listen-Zeile zeigt Name + E-Mail und (wie SOURCE) einen 📞-Anruf-Link rechts. Die 🚗/🧾-Zähler +
 * letztes Auftragsdatum der SOURCE fehlen im CustomerDTO (backend-blockiert, gemeldet, nicht erfunden).
 * Die DSGVO-Aktionen (Anonymisieren/Export) folgen als eigener gated Schritt; hier der Kern plus die
 * Anruf-Aktion: Liste, Detail, Anlegen, Stammdaten ändern, anrufen.
 */
export function CustomersPage() {
  const list = useCustomersList()
  const create = useCreateCustomer()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  const [query, setQuery] = useState('')

  const customers = list.data?.customers ?? []
  const q = query.trim().toLowerCase()
  const filtered = q
    ? customers.filter((c) => [c.name, c.phone, c.email].some((field) => (field ?? '').toLowerCase().includes(q)))
    : customers

  function startCreate() {
    setCreating(true)
    setSelectedId(null)
  }

  function submitCreate(fields: CustomerFields) {
    create.mutate(
      {
        name: fields.name,
        phone: fields.phone || null,
        email: fields.email || null,
        address: fields.address || null,
        client_uuid: newUuid(),
      },
      {
        onSuccess: (res) => {
          setCreating(false)
          setSelectedId(res.customer.id)
        },
      },
    )
  }

  return (
    <section className="wf-view">
      <div className="wf-viewhead">
        <div>
          <h2 className="viewtitle">Kunden</h2>
          <p className="sub">Alle Kunden &amp; Fahrzeuge · suchen, Anschrift pflegen, Historie</p>
        </div>
        <button type="button" className="wf-btn wf-btn--primary wf-intake__toggle" onClick={startCreate}>
          ＋ Neuer Kunde
        </button>
      </div>

      <div className="wf-split">
        <div>
          <input
            className="wf-input"
            type="search"
            placeholder="Suchen: Name, Telefon, E-Mail"
            aria-label="Kunden durchsuchen"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="wf-sectionhead">
            <h3>Kundenliste</h3>
            <span className="wf-count">{filtered.length}</span>
          </div>
          {list.isLoading ? (
            <div className="muted">Kunden werden geladen…</div>
          ) : list.error ? (
            <div className="error" role="alert">
              Kunden konnten nicht geladen werden.
            </div>
          ) : filtered.length === 0 ? (
            <div className="wf-empty">
              <div className="wf-empty__title">{q ? 'Keine Treffer' : 'Noch keine Kunden'}</div>
              <div className="wf-empty__text">
                {q ? 'Andere Suche versuchen.' : 'Kunden entstehen beim Auftrag-Anlegen, oder leg oben einen an.'}
              </div>
            </div>
          ) : (
            <div className="wf-splitlist">
              {filtered.map((customer) => (
                <div
                  key={customer.id}
                  className={selectedId === customer.id ? 'wf-listrow wf-listrow--active' : 'wf-listrow'}
                >
                  <button
                    type="button"
                    className="wf-listrow__select"
                    aria-pressed={selectedId === customer.id}
                    onClick={() => {
                      setSelectedId(customer.id)
                      setCreating(false)
                    }}
                  >
                    <span className="wf-listrow__avatar" aria-hidden="true">
                      {initials(customer.name)}
                    </span>
                    <span className="wf-listrow__main">
                      <span className="wf-listrow__title">{customer.name}</span>
                      {/* SOURCE zeigt hier 🚗/🧾-Zähler + letztes Auftragsdatum — die fehlen im CustomerDTO
                          (backend-blockiert, nicht erfunden). Bis dahin die E-Mail als zweite Kennung. */}
                      {customer.email ? <span className="wf-card__meta">{customer.email}</span> : null}
                    </span>
                  </button>
                  {customer.phone ? <CallIconLink phone={customer.phone} name={customer.name} /> : null}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="wf-detailpane">
          {creating ? (
            <>
              <div className="wf-detailhead">
                <span className="wf-listrow__avatar" aria-hidden="true">
                  ＋
                </span>
                <div className="wf-detailhead__main">
                  <div className="wf-detailhead__title">Neuer Kunde</div>
                  <div className="wf-card__meta">Stammdaten anlegen</div>
                </div>
              </div>
              <CustomerForm
                initial={EMPTY_CUSTOMER}
                submitLabel="Anlegen"
                submitting={create.isPending}
                error={create.error instanceof Error ? create.error.message : null}
                onCancel={() => setCreating(false)}
                onSubmit={submitCreate}
              />
            </>
          ) : selectedId != null ? (
            <CustomerDetailPane id={selectedId} onDeleted={() => setSelectedId(null)} />
          ) : (
            <div className="wf-empty">
              <div className="wf-empty__title">Kunde wählen</div>
              <div className="wf-empty__text">Links einen Kunden antippen — oder oben einen neuen anlegen.</div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
